import Slider from '@mui/material/Slider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImg, { dataURLtoFile } from '../../common/cropImage';
import { detectHEIC } from '../../common/fileReaderSupport';
import { uploadFile } from '../../common/fileUpload';
import { base64ToBlob } from '../../common/imageTypeConverter';
import { resizeFile } from '../../common/resizeImage';
import { PATH_NAME } from '../../configs/pathName';
import { ERROR_LOGO, SUCCESS_LOGO } from '../../constant/imgImport';
import { MAX_CANVAS_SIZE } from '../../constant/numericParams';
import { delayNavigate } from '../../helpers/delayNavigate';
import AuthContext from '../../state/auth/AuthContext';
import { useModal } from '../../state/modal-provider/ModalProvider';

type Props = {
  isVisible?: boolean | string;
  toggle: Function;
  classname?: string;
  type?: string;
  handleSetTemp: Function;
  oldAvatar: any;
};

const UserAvtModal = ({
  isVisible,
  toggle,
  classname,
  handleSetTemp,
  oldAvatar,
}: Props) => {
  const inputRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [image, setImage] = useState<any>(null); //this should be in base64 format. only use this to display image
  const [croppedArea, setCroppedArea] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<any>(1);
  const [uploadStatus, setUploadStatus] = useState<boolean | null>(null); //status from aws, notify if avt posted successfully or not
  const [minimizedImage, setMinimizedImage] = useState(''); //url of the compressed avt
  const [errModal, setErrModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const { toast } = useModal();

  const { setLoading } = useContext(AuthContext);

  const closeModal = (event: any) => {
    toggle();
    event.preventDefault();
  };

  const onCropComplete = (
    croppedAreaPercentage: any,
    croppedAreaPixels: any
  ) => {
    setCroppedArea(croppedAreaPixels);
  };

  const onSelectFile = async (event: any) => {
    try {
      setLoading(true);
      if (event.target.files && event.target.files.length > 0) {
        const reader: any = new FileReader();
        await reader.readAsDataURL(event.target.files[0]);

        await reader.addEventListener('load', async () => {
          if (reader?.result !== null) {
            let imgFileSrc = reader?.result.toString();
            imgFileSrc = await detectHEIC(imgFileSrc);
            imgFileSrc = await resizeFile({
              file: base64ToBlob(imgFileSrc),
              maxWidth: MAX_CANVAS_SIZE,
              maxHeight: MAX_CANVAS_SIZE,
            });
            setImage(imgFileSrc);
            setLoading(false);
          }
        });
      }
    } catch (err) {
      setErrModal(true);
      setLoading(false);
    }
  };

  const uploadImgToServer = async (dataUrl: string) => {
    setLoading(true);

    const blobImg = base64ToBlob(dataUrl);
    const minimizedImage: any = await resizeFile({ file: blobImg });
    const convertedToFile = dataURLtoFile(minimizedImage, 'useravt.png');

    try {
      uploadFile(
        convertedToFile,
        handleSetTemp,
        setUploadStatus,
        oldAvatar
      ).then(() => {
        toggle(false);
        setLoading(false);
      }); //CANNOT use await to replace this .then, due to limitation of handler
      setLoading(false);
      return minimizedImage;
    } catch (err) {
      setErrModal(true);
      setLoading(false);
      console.log(err);
      delayNavigate(PATH_NAME.HOME);
    }
  };

  const handleSubmit = async (imageSrc: any, crop: any) => {
    setLoading(true);

    const canvas: any = await getCroppedImg(imageSrc, crop);
    const dataUrl = canvas.toDataURL();

    const compressedImageToUpload: any = await uploadImgToServer(dataUrl);
    if (compressedImageToUpload) setMinimizedImage(compressedImageToUpload);
    setLoading(false);
  };

  useEffect(() => {
    if (uploadStatus === true) {
      handleSetTemp(minimizedImage);
      setSuccessModal(true);
    } else if (uploadStatus === false) {
      setErrModal(true);
    }
    setImage(null);
  }, [uploadStatus, oldAvatar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errModal) {
      toast.notificate({
        content: 'Đã có lỗi xảy ra, vui lòng thử lại sau',
        infinite: true,
        image: ERROR_LOGO,
      });
      setErrModal(false);
    }
    if (successModal) {
      toast.notificate({
        content: 'Cập nhật ảnh đại diện thành công',
        infinite: true,
        image: SUCCESS_LOGO,
      });
      setSuccessModal(false);
    }
  }, [errModal, successModal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={`${
        isVisible ? 'visible' : 'invisible'
      } flex justify-center items-center fixed left-0 right-0 top-0 bottom-0 z-[400]`}
    >
      <div
        onClick={closeModal}
        className="bg-black opacity-40 w-full h-full"
      ></div>
      <div
        className={`${classname} max-w-sm w-5/6 p-3 flex flex-col bg-white rounded-2xl top-2/5 absolute justify-center items-center overflow-hidden`}
      >
        {image && (
          <>
            <div className="space-y-6 z-10 relative w-full">
              <div className="cropper h-72">
                <Cropper
                  image={image} //accespt base64 format
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
            </div>
            <Slider
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e, zoom) => setZoom(zoom)}
            />
          </>
        )}

        <div className="flex justify-center gap-5 w-full p-5">
          <button
            className="btn blue-gradient max-w-[90%]"
            onClick={() => inputRef?.current?.click()}
          >
            Chọn ảnh
          </button>
          {image && (
            <button
              className="btn blue-gradient max-w-[90%]"
              onClick={async () => {
                await handleSubmit(image, croppedArea);
              }}
            >
              Đồng ý
            </button>
          )}
        </div>
      </div>

      <input
        type="file"
        ref={inputRef}
        onChange={onSelectFile}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default UserAvtModal;
